export type VideoContainer = 'mp4' | 'webm';

export type VideoFormat = {
  container: VideoContainer;
  /** Value passed to MediaRecorder. */
  mimeType: string;
  codec: string | null;
};

/** MP4 first, WebM second, most specific first within each. */
export const VIDEO_FORMAT_CANDIDATES: readonly VideoFormat[] = [
  { container: 'mp4', mimeType: 'video/mp4;codecs=avc1.42E01E', codec: 'avc1.42E01E' },
  { container: 'mp4', mimeType: 'video/mp4;codecs=avc1', codec: 'avc1' },
  { container: 'mp4', mimeType: 'video/mp4', codec: null },
  { container: 'webm', mimeType: 'video/webm;codecs=vp9', codec: 'vp9' },
  { container: 'webm', mimeType: 'video/webm;codecs=vp8', codec: 'vp8' },
  { container: 'webm', mimeType: 'video/webm', codec: null },
];

export type IsTypeSupported = (mimeType: string) => boolean;

export function browserIsTypeSupported(): IsTypeSupported | null {
  const recorder = (globalThis as { MediaRecorder?: { isTypeSupported?: IsTypeSupported } })
    .MediaRecorder;
  if (typeof recorder?.isTypeSupported !== 'function') {
    return null;
  }
  return (mimeType) => recorder.isTypeSupported?.(mimeType) ?? false;
}

export function supportedVideoFormats(isTypeSupported: IsTypeSupported | null): VideoFormat[] {
  if (!isTypeSupported) {
    return [];
  }
  return VIDEO_FORMAT_CANDIDATES.filter((format) => {
    try {
      return isTypeSupported(format.mimeType);
    } catch {
      return false;
    }
  });
}

/** Pick the requested container if supported, else MP4, else WebM. */
export function selectVideoFormat(
  isTypeSupported: IsTypeSupported | null,
  preferred?: VideoContainer,
): VideoFormat | null {
  const supported = supportedVideoFormats(isTypeSupported);
  if (preferred) {
    return supported.find((format) => format.container === preferred) ?? null;
  }
  return supported[0] ?? null;
}

/** MediaRecorder may report a generic or more specific type than requested. */
export function describeRecordedFormat(
  requested: VideoFormat,
  recordedMimeType: string | undefined,
): VideoFormat {
  const mimeType = recordedMimeType || requested.mimeType;
  const [base, ...parameters] = mimeType.split(';').map((part) => part.trim());
  const codecs = parameters
    .find((part) => part.startsWith('codecs='))
    ?.slice('codecs='.length)
    .replace(/"/g, '');
  return {
    container: base === 'video/mp4' ? 'mp4' : base === 'video/webm' ? 'webm' : requested.container,
    mimeType: base,
    codec: codecs || requested.codec,
  };
}
